import type { FolderRecord } from '../../data-binding/models/FolderRecord';
import type { AllocationLoadStatus } from './OrgAllocationService';
import {
  OrgPersonWorkContextService,
  type OrgPersonWorkContext,
  type OrgPersonWorkContextAssignment,
  type OrgPersonWorkContextNode,
} from './OrgPersonWorkContextService';
import {
  DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
  normalizeOrgWorkloadFilterKey,
  orgWorkloadWindowOverlapRatio,
  parseOrgWorkloadDate,
  resolveOrgWorkloadWindow,
  type OrgWorkloadLensSettings,
  type OrgWorkloadResolvedWindow,
} from '../models/OrgWorkloadLens';
import type { OrgCapacityScenarioDraft } from './OrgCapacityScenarioDraftService';
import type { OrgCapacityScenarioCommitmentInput } from './OrgAllocationScenarioService';

export const ORG_WORKLOAD_LENS_FIELDS = [
  'workloadDisplayAllocationPercent',
  'workloadDisplayAllocationLabel',
  'workloadAllocationPercent',
  'workloadAllocationLabel',
  'workloadStatus',
  'workloadTiming',
  'workloadCommitmentCount',
  'workloadTargetLabel',
  'workloadWindowLabel',
  'workloadSummary',
  'workloadAvailablePercent',
  'workloadAvailableLabel',
  'workloadProjectedAllocationPercent',
  'workloadProjectedAllocationLabel',
  'workloadCandidateTargetLabel',
  'workloadCandidateStatus',
  'workloadCandidateSummary',
  'workloadAlreadyOnCandidateTarget',
  'workloadInCandidateDraft',
] as const;

export type OrgWorkloadLensStatus = AllocationLoadStatus | 'scheduled';
export type OrgWorkloadLensTiming = 'current' | 'scheduled' | 'window';
export type OrgWorkloadCandidateStatus =
  | 'available'
  | 'tight'
  | 'over_capacity'
  | 'already_assigned'
  | 'in_draft'
  | 'unknown';

export interface OrgWorkloadLensFieldServiceOptions {
  readonly asOf?: Date | string;
  readonly capacityTarget?: number;
  readonly workloadLensSettings?: OrgWorkloadLensSettings;
  readonly scenarioDraft?: OrgCapacityScenarioDraft | null;
}

interface ResolvedLensOptions {
  readonly asOf: Date;
  readonly capacityTarget: number;
  readonly window: OrgWorkloadResolvedWindow;
  readonly targetKinds: ReadonlySet<string>;
  readonly targetExternalIds: ReadonlySet<string>;
  readonly sourceKeys: ReadonlySet<string>;
  readonly candidateTargetKind: string | null;
  readonly candidateTargetExternalId: string | null;
  readonly candidateTargetLabel: string | null;
  readonly candidateProposedAllocation: number | null;
  readonly scenarioDraft: OrgCapacityScenarioDraft | null;
}

const DEFAULT_CAPACITY_TARGET = 1;

/**
 * Transient lens fields for source-backed Atlas workload facts.
 *
 * The service projects folder relationship records onto org tree nodes for
 * rendering only; it does not mutate node attrs or make workload a persisted
 * orgchart field.
 */
export class OrgWorkloadLensFieldService {
  constructor(private readonly workContextService = new OrgPersonWorkContextService()) {}

  buildFieldMap(
    nodes: readonly OrgPersonWorkContextNode[],
    relationshipRecords: readonly FolderRecord[],
    options: OrgWorkloadLensFieldServiceOptions = {},
  ): Map<string, Record<string, unknown>> {
    const resolvedOptions = resolveLensOptions(options);
    const out = new Map<string, Record<string, unknown>>();

    for (const node of nodes) {
      const context = this.workContextService.buildContext(node, relationshipRecords, options);
      const fields = fieldsForContext(context, resolvedOptions);
      if (fields) out.set(node.id, fields);
    }

    return out;
  }
}

function fieldsForContext(
  context: OrgPersonWorkContext,
  options: ResolvedLensOptions,
): Record<string, unknown> | null {
  if (!context.personNodeId) return null;

  const matchedAssignments = [
    ...context.activeAssignments,
    ...context.inactiveAssignments,
  ].filter((assignment) => assignmentMatchesFilters(assignment, options));
  const draftAdditions = draftAdditionsForContext(context, options);

  if (matchedAssignments.length === 0) {
    return hasCandidateTarget(options)
      ? candidateFieldsForNoKnownWorkload(options, draftAdditions)
      : null;
  }

  if (options.window.granularity !== 'point') {
    const windowAssignments = matchedAssignments.filter((assignment) =>
      assignmentOverlapsWindow(assignment, options.window));
    if (windowAssignments.length === 0) {
      return hasCandidateTarget(options)
        ? candidateFieldsForAssignments(
          [],
          0,
          false,
          matchedAssignments,
          options,
          draftAdditions,
        )
        : null;
    }

    const allocation = sumWindowAllocations(windowAssignments, options.window);
    return fieldsForAssignments(
      windowAssignments,
      allocation,
      loadStatus(
        allocation,
        windowAssignments.some((assignment) => assignment.hasUnspecifiedAllocation),
        options.capacityTarget,
      ),
      'window',
      options.window.label,
      matchedAssignments,
      options,
      draftAdditions,
    );
  }

  const activeAssignments = matchedAssignments.filter((assignment) =>
    isActiveAt(assignment, options.asOf));
  if (activeAssignments.length > 0) {
    const allocation = sumKnownAllocations(activeAssignments);
    return fieldsForAssignments(
      activeAssignments,
      allocation,
      loadStatus(
        allocation,
        activeAssignments.some((assignment) => assignment.hasUnspecifiedAllocation),
        options.capacityTarget,
      ),
      'current',
      options.window.label,
      matchedAssignments,
      options,
      draftAdditions,
    );
  }

  const nextScheduled = nextScheduledAssignments(matchedAssignments, options.asOf);
  if (nextScheduled.length === 0) {
    return hasCandidateTarget(options)
      ? candidateFieldsForAssignments(
        [],
        0,
        false,
        matchedAssignments,
        options,
        draftAdditions,
      )
      : null;
  }

  return fieldsForAssignments(
    nextScheduled,
    sumKnownAllocations(nextScheduled),
    'scheduled',
    'scheduled',
    nextScheduledLabel(nextScheduled),
    matchedAssignments,
    options,
    draftAdditions,
  );
}

function fieldsForAssignments(
  assignments: readonly OrgPersonWorkContextAssignment[],
  allocation: number,
  status: OrgWorkloadLensStatus,
  timing: OrgWorkloadLensTiming,
  windowLabel: string,
  matchedAssignments: readonly OrgPersonWorkContextAssignment[],
  options: ResolvedLensOptions,
  draftAdditions: readonly OrgCapacityScenarioCommitmentInput[],
): Record<string, unknown> {
  const hasKnownAllocation = assignments.some((assignment) => !assignment.hasUnspecifiedAllocation);
  const allocationPercent = hasKnownAllocation
    ? Math.round(allocation * 100)
    : null;
  const allocationLabel = allocationPercent != null ? `${allocationPercent}%` : null;
  const targetLabel = targetSummary(assignments);
  const timingLabel = timing === 'current'
    ? 'current'
    : timing === 'scheduled'
      ? 'scheduled'
      : windowLabel;
  const summaryParts = [
    allocationLabel ? `${allocationLabel} ${timingLabel}` : capitalize(timingLabel),
    targetLabel,
  ].filter(Boolean);
  const workloadSummary = summaryParts.join(' · ');

  return {
    ...(allocationPercent != null ? { workloadDisplayAllocationPercent: allocationPercent } : {}),
    ...(allocationLabel ? { workloadDisplayAllocationLabel: allocationLabel } : {}),
    ...(allocationPercent != null ? { workloadAllocationPercent: allocationPercent } : {}),
    ...(allocationLabel ? { workloadAllocationLabel: allocationLabel } : {}),
    workloadStatus: status,
    workloadTiming: timing,
    workloadCommitmentCount: assignments.length,
    workloadTargetLabel: targetLabel,
    workloadWindowLabel: windowLabel,
    workloadSummary,
    workloadCandidateSummary: workloadSummary,
    ...candidateFieldsForAssignments(
      timing === 'scheduled' ? [] : assignments,
      timing === 'scheduled' ? 0 : allocation,
      assignments.some((assignment) => assignment.hasUnspecifiedAllocation),
      matchedAssignments,
      options,
      draftAdditions,
    ),
  };
}

function resolveLensOptions(options: OrgWorkloadLensFieldServiceOptions): ResolvedLensOptions {
  const asOf = parseOrgWorkloadDate(options.asOf) ?? parseOrgWorkloadDate(new Date()) ?? new Date();
  const settings = {
    ...DEFAULT_ORG_WORKLOAD_LENS_SETTINGS,
    ...(options.workloadLensSettings ?? {}),
  };

  return {
    asOf,
    capacityTarget: clampCapacityTarget(options.capacityTarget),
    window: resolveOrgWorkloadWindow(settings, asOf),
    targetKinds: optionSet(settings.targetKinds),
    targetExternalIds: optionSet(settings.targetExternalIds),
    sourceKeys: optionSet(settings.sourceKeys),
    candidateTargetKind: stringValue(settings.candidateTargetKind),
    candidateTargetExternalId: stringValue(settings.candidateTargetExternalId),
    candidateTargetLabel: stringValue(settings.candidateTargetLabel),
    candidateProposedAllocation: normalizeAllocation(settings.candidateProposedAllocation),
    scenarioDraft: options.scenarioDraft ?? null,
  };
}

function hasCandidateTarget(options: ResolvedLensOptions): boolean {
  return Boolean(options.candidateTargetExternalId || options.candidateTargetLabel);
}

function candidateFieldsForNoKnownWorkload(
  options: ResolvedLensOptions,
  draftAdditions: readonly OrgCapacityScenarioCommitmentInput[] = [],
): Record<string, unknown> {
  if (!hasCandidateTarget(options)) return {};
  if (draftAdditions.length > 0) {
    return candidateFieldsForAssignments([], 0, false, [], options, draftAdditions);
  }
  return {
    workloadCandidateTargetLabel: candidateTargetLabel(options),
    workloadCandidateStatus: 'unknown',
    workloadCandidateSummary: 'No known load for this window',
    workloadAlreadyOnCandidateTarget: false,
    workloadInCandidateDraft: false,
  };
}

function candidateFieldsForAssignments(
  assignmentsInWindow: readonly OrgPersonWorkContextAssignment[],
  currentAllocation: number,
  hasUnspecifiedAllocation: boolean,
  matchedAssignments: readonly OrgPersonWorkContextAssignment[],
  options: ResolvedLensOptions,
  draftAdditions: readonly OrgCapacityScenarioCommitmentInput[] = [],
): Record<string, unknown> {
  if (!hasCandidateTarget(options)) return {};

  const proposedAllocation = options.candidateProposedAllocation ?? 0.25;
  const draftAllocation = sumDraftAllocationsForWindow(draftAdditions, options.window);
  const hasDraftAddition = draftAdditions.length > 0;
  const hasUnknownDraftAllocation = draftAdditions.some((addition) =>
    numericDraftAllocation(addition.allocation) == null);
  const availableAllocation = Math.max(0, options.capacityTarget - currentAllocation);
  const alreadyAssigned = isAlreadyAssignedToCandidateTarget(assignmentsInWindow, options);
  const projectedAllocation = alreadyAssigned && !hasDraftAddition
    ? currentAllocation
    : currentAllocation + (hasDraftAddition ? draftAllocation : proposedAllocation);
  const status = candidateStatus(
    projectedAllocation,
    hasUnspecifiedAllocation || hasUnknownDraftAllocation,
    alreadyAssigned,
    hasDraftAddition,
    options.capacityTarget,
  );
  const availablePercent = Math.round(availableAllocation * 100);
  const projectedPercent = Math.round(projectedAllocation * 100);
  const targetLabel = candidateTargetLabel(options);

  return {
    workloadDisplayAllocationPercent: projectedPercent,
    workloadDisplayAllocationLabel: `${projectedPercent}%`,
    workloadAvailablePercent: availablePercent,
    workloadAvailableLabel: `${availablePercent}%`,
    workloadProjectedAllocationPercent: projectedPercent,
    workloadProjectedAllocationLabel: `${projectedPercent}%`,
    workloadCandidateTargetLabel: targetLabel,
    workloadCandidateStatus: status,
    workloadCandidateSummary: candidateSummary(
      status,
      availablePercent,
      projectedPercent,
      targetLabel,
      matchedAssignments,
      hasDraftAddition,
    ),
    workloadAlreadyOnCandidateTarget: alreadyAssigned,
    workloadInCandidateDraft: hasDraftAddition,
  };
}

function candidateStatus(
  projectedAllocation: number,
  hasUnspecifiedAllocation: boolean,
  alreadyAssigned: boolean,
  hasDraftAddition: boolean,
  capacityTarget: number,
): OrgWorkloadCandidateStatus {
  if (hasDraftAddition) return 'in_draft';
  if (alreadyAssigned) return 'already_assigned';
  if (hasUnspecifiedAllocation && projectedAllocation === 0) return 'unknown';
  if (projectedAllocation > capacityTarget + 0.0001) return 'over_capacity';
  if (projectedAllocation >= capacityTarget * 0.85) return 'tight';
  return 'available';
}

function candidateSummary(
  status: OrgWorkloadCandidateStatus,
  availablePercent: number,
  projectedPercent: number,
  targetLabel: string,
  matchedAssignments: readonly OrgPersonWorkContextAssignment[],
  hasDraftAddition: boolean,
): string {
  if (hasDraftAddition) return `In draft · ${projectedPercent}% if applied`;
  if (status === 'already_assigned') return `Already on ${targetLabel}`;
  if (status === 'unknown') return 'Load unknown';
  const prefix = status === 'over_capacity'
    ? 'Would overload'
    : status === 'tight'
      ? 'Tight fit'
      : 'Available';
  const context = matchedAssignments.length > 1
    ? ` across ${matchedAssignments.length} commitments`
    : '';
  return `${prefix} · ${availablePercent}% free · ${projectedPercent}% if added${context}`;
}

function candidateTargetLabel(options: ResolvedLensOptions): string {
  return options.candidateTargetLabel ??
    options.candidateTargetExternalId ??
    'selected target';
}

function isAlreadyAssignedToCandidateTarget(
  assignments: readonly OrgPersonWorkContextAssignment[],
  options: ResolvedLensOptions,
): boolean {
  const targetId = normalizeOrgWorkloadFilterKey(options.candidateTargetExternalId);
  const targetKind = normalizeOrgWorkloadFilterKey(options.candidateTargetKind);
  if (!targetId && !targetKind) return false;

  return assignments.some((assignment) => {
    const assignmentTargetId = normalizeOrgWorkloadFilterKey(assignment.targetExternalId);
    const assignmentTargetKind = normalizeOrgWorkloadFilterKey(assignment.targetKind);
    if (targetId && assignmentTargetId !== targetId) return false;
    if (targetKind && assignmentTargetKind !== targetKind) return false;
    return true;
  });
}

function draftAdditionsForContext(
  context: OrgPersonWorkContext,
  options: ResolvedLensOptions,
): OrgCapacityScenarioCommitmentInput[] {
  if (!options.scenarioDraft || !hasCandidateTarget(options)) return [];

  return (options.scenarioDraft.delta.additions ?? [])
    .filter((addition) =>
      draftAdditionMatchesCandidateTarget(addition, options) &&
      draftAdditionMatchesContext(addition, context) &&
      draftAdditionOverlapsWindow(addition, options),
    );
}

function draftAdditionMatchesCandidateTarget(
  addition: OrgCapacityScenarioCommitmentInput,
  options: ResolvedLensOptions,
): boolean {
  const targetId = normalizeOrgWorkloadFilterKey(options.candidateTargetExternalId);
  const targetKind = normalizeOrgWorkloadFilterKey(options.candidateTargetKind);
  if (targetId && normalizeOrgWorkloadFilterKey(addition.targetExternalId) !== targetId) return false;
  if (targetKind && normalizeOrgWorkloadFilterKey(addition.targetKind) !== targetKind) return false;
  return true;
}

function draftAdditionMatchesContext(
  addition: OrgCapacityScenarioCommitmentInput,
  context: OrgPersonWorkContext,
): boolean {
  const actorExternalId = stringValue(addition.actorExternalId);
  if (!actorExternalId) return false;

  const actorToken = normalizeOrgWorkloadFilterKey(actorExternalId);
  const actorSafeSegment = safeSegment(actorExternalId);
  const nodeActorToken = context.personNodeId
    ? normalizeOrgWorkloadFilterKey(`org-node:${context.personNodeId}`)
    : '';
  if (nodeActorToken && actorToken === nodeActorToken) return true;

  for (const candidate of context.identityCandidates) {
    if (!candidate.strong) continue;
    const candidateToken = normalizeOrgWorkloadFilterKey(candidate.value);
    const candidateNormalizedToken = normalizeOrgWorkloadFilterKey(candidate.token);
    const candidateSafeToken = normalizeOrgWorkloadFilterKey(candidate.sourceScopedToken);
    if (actorToken === candidateToken) return true;
    if (actorToken === candidateNormalizedToken) return true;
    if (actorToken === candidateSafeToken) return true;
    if (actorSafeSegment === candidate.sourceScopedToken) return true;
    if (candidate.sourceScopedToken.length >= 3 && actorSafeSegment.includes(candidate.sourceScopedToken)) {
      return true;
    }
    if (candidateNormalizedToken.length >= 3 && actorToken.includes(candidateNormalizedToken)) {
      return true;
    }
  }

  return false;
}

function draftAdditionOverlapsWindow(
  addition: OrgCapacityScenarioCommitmentInput,
  options: ResolvedLensOptions,
): boolean {
  if (options.window.granularity === 'point') {
    return isDraftActiveAt(addition, options.asOf);
  }
  return orgWorkloadWindowOverlapRatio(
    options.window,
    parseOrgWorkloadDate(addition.validFrom),
    parseOrgWorkloadDate(addition.validTo),
  ) > 0;
}

function isDraftActiveAt(
  addition: OrgCapacityScenarioCommitmentInput,
  asOf: Date,
): boolean {
  const start = parseOrgWorkloadDate(addition.validFrom);
  const end = parseOrgWorkloadDate(addition.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function sumDraftAllocationsForWindow(
  additions: readonly OrgCapacityScenarioCommitmentInput[],
  window: OrgWorkloadResolvedWindow,
): number {
  return additions.reduce((sum, addition) => {
    const allocation = numericDraftAllocation(addition.allocation);
    if (allocation == null) return sum;
    if (window.granularity === 'point') return sum + allocation;
    return sum + allocation * orgWorkloadWindowOverlapRatio(
      window,
      parseOrgWorkloadDate(addition.validFrom),
      parseOrgWorkloadDate(addition.validTo),
    );
  }, 0);
}

function numericDraftAllocation(value: OrgCapacityScenarioCommitmentInput['allocation']): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const numeric = Number(value.trim());
  return Number.isFinite(numeric) ? numeric : null;
}

function assignmentMatchesFilters(
  assignment: OrgPersonWorkContextAssignment,
  options: ResolvedLensOptions,
): boolean {
  if (
    options.targetKinds.size > 0 &&
    !options.targetKinds.has(normalizeOrgWorkloadFilterKey(assignment.targetKind))
  ) {
    return false;
  }
  if (
    options.targetExternalIds.size > 0 &&
    !options.targetExternalIds.has(normalizeOrgWorkloadFilterKey(assignment.targetExternalId))
  ) {
    return false;
  }
  if (
    options.sourceKeys.size > 0 &&
    !sourceKeysForAssignment(assignment).some((key) => options.sourceKeys.has(key))
  ) {
    return false;
  }
  return true;
}

function assignmentOverlapsWindow(
  assignment: OrgPersonWorkContextAssignment,
  window: OrgWorkloadResolvedWindow,
): boolean {
  return orgWorkloadWindowOverlapRatio(
    window,
    parseOrgWorkloadDate(assignment.validFrom),
    parseOrgWorkloadDate(assignment.validTo),
  ) > 0;
}

function sumWindowAllocations(
  assignments: readonly OrgPersonWorkContextAssignment[],
  window: OrgWorkloadResolvedWindow,
): number {
  return assignments.reduce((sum, assignment) => {
    if (assignment.allocation == null) return sum;
    const ratio = orgWorkloadWindowOverlapRatio(
      window,
      parseOrgWorkloadDate(assignment.validFrom),
      parseOrgWorkloadDate(assignment.validTo),
    );
    return sum + assignment.allocation * ratio;
  }, 0);
}

function sourceKeysForAssignment(assignment: OrgPersonWorkContextAssignment): string[] {
  const source = isRecord(assignment.source) ? assignment.source : {};
  return uniqueStrings([
    normalizeOrgWorkloadFilterKey(assignment.sourceLabel),
    normalizeOrgWorkloadFilterKey(assignment.sourceSystem),
    normalizeOrgWorkloadFilterKey(source.label),
    normalizeOrgWorkloadFilterKey(source.connectorId),
    normalizeOrgWorkloadFilterKey(source.sourceSystem),
    normalizeOrgWorkloadFilterKey(source.mappingRef),
  ]);
}

function nextScheduledAssignments(
  assignments: readonly OrgPersonWorkContextAssignment[],
  asOf: Date,
): OrgPersonWorkContextAssignment[] {
  const futureStarts = assignments
    .map((assignment) => parseOrgWorkloadDate(assignment.validFrom))
    .filter((date): date is Date => !!date && date.getTime() > asOf.getTime())
    .sort((left, right) => left.getTime() - right.getTime());
  const nextStart = futureStarts[0];
  if (!nextStart) return [];
  return assignments
    .filter((assignment) => isActiveAt(assignment, nextStart))
    .sort(compareAssignments);
}

function isActiveAt(assignment: OrgPersonWorkContextAssignment, asOf: Date): boolean {
  const start = parseOrgWorkloadDate(assignment.validFrom);
  const end = parseOrgWorkloadDate(assignment.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function compareAssignments(
  left: OrgPersonWorkContextAssignment,
  right: OrgPersonWorkContextAssignment,
): number {
  return left.targetKind.localeCompare(right.targetKind) ||
    left.targetLabel.localeCompare(right.targetLabel);
}

function targetSummary(assignments: readonly OrgPersonWorkContextAssignment[]): string {
  const targets = uniqueStrings(assignments.map((assignment) => assignment.targetLabel));
  if (targets.length === 0) return '';
  if (targets.length === 1) return targets[0];
  return `${targets.length} work items`;
}

function sumKnownAllocations(assignments: readonly OrgPersonWorkContextAssignment[]): number {
  return assignments.reduce((sum, assignment) => sum + (assignment.allocation ?? 0), 0);
}

function nextScheduledLabel(assignments: readonly OrgPersonWorkContextAssignment[]): string {
  const nextStart = assignments
    .map((assignment) => parseOrgWorkloadDate(assignment.validFrom))
    .filter((date): date is Date => !!date)
    .sort((left, right) => left.getTime() - right.getTime())[0];
  if (!nextStart) return 'scheduled';
  return `from ${new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(nextStart)}`;
}

function loadStatus(
  totalAllocation: number,
  hasUnspecifiedAllocation: boolean,
  capacityTarget: number,
): AllocationLoadStatus {
  if (hasUnspecifiedAllocation && totalAllocation === 0) return 'unknown';
  if (totalAllocation > capacityTarget + 0.0001) return 'over_allocated';
  if (Math.abs(totalAllocation - capacityTarget) <= 0.0001) return 'fully_allocated';
  return 'under_allocated';
}

function clampCapacityTarget(value: number | undefined): number {
  if (!value || !Number.isFinite(value) || value <= 0) return DEFAULT_CAPACITY_TARGET;
  return Math.max(0.01, Math.min(10, value));
}

function normalizeAllocation(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return Math.max(0.01, Math.min(1, value));
}

function stringValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function optionSet(values: readonly string[] | undefined): Set<string> {
  return new Set((values ?? [])
    .map((value) => normalizeOrgWorkloadFilterKey(value))
    .filter(Boolean));
}

function uniqueStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function safeSegment(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
